/**
 * Semantic summary engine — public entry point.
 *
 * `buildSummaryRenderPlan` runs the full pipeline:
 *   ingest → analyze → extract metrics → derive insights → layout
 *
 * The result is a declarative plan the UI renders without knowing anything
 * about the payload's schema.
 */

import { analyzeTree, tokenizeKey } from "./analyze";
import { formatProseParagraphs, humanizeEnum, sanitizeText } from "./format";
import { ingestSummaryPayload } from "./ingest";
import { deriveInsights } from "./insights";
import { buildLayout } from "./layout";
import { extractMetrics } from "./metrics";
import type { TSemanticNode, TSummaryRenderPlan } from "./types";

export type {
  TRenderBlock,
  TRendererKind,
  TRenderSection,
  TSemanticNode,
  TSummaryInsight,
  TSummaryMetric,
  TSummaryRenderPlan,
} from "./types";

const HERO_SUMMARY_TOKENS = new Set(["summary", "overview", "executive", "description", "about"]);

function isHeroSummaryNode(node: TSemanticNode): boolean {
  return (
    node.path.length === 1 &&
    (node.dataType === "longText" || node.dataType === "shortText") &&
    tokenizeKey(node.key).some((t) => HERO_SUMMARY_TOKENS.has(t))
  );
}

function findHeroTitle(nodes: TSemanticNode[]): string | null {
  for (const node of nodes) {
    if (node.path.length === 1 && node.concept === "identity" && node.dataType === "shortText") {
      if (tokenizeKey(node.key).some((t) => t === "name" || t === "display")) {
        return sanitizeText(String(node.value));
      }
    }
  }
  return null;
}

function findHeroSubtitle(nodes: TSemanticNode[]): string | null {
  for (const node of nodes) {
    if (
      node.path.length === 1 &&
      node.concept === "identity" &&
      (node.dataType === "shortText" || node.dataType === "enum") &&
      tokenizeKey(node.key).includes("type")
    ) {
      return humanizeEnum(String(node.value));
    }
  }
  return null;
}

function collectRecommendations(nodes: TSemanticNode[]): { texts: string[]; keys: string[] } {
  const entries = nodes
    .filter(
      (node) =>
        node.path.length === 1 &&
        node.concept === "recommendation" &&
        typeof node.value === "string" &&
        String(node.value).trim().length > 0
    )
    .sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));
  return {
    texts: entries.map((node) => sanitizeText(String(node.value).trim())),
    keys: entries.map((node) => node.key),
  };
}

export type TBuildPlanInput = {
  /** The raw `summary` payload from the API (string or object). */
  raw: unknown;
  /** Company display name — hero fallback when the payload has no name. */
  companyName?: string | null;
  /** FUNDRAISER | INVESTOR — reserved for role-specific tuning. */
  companyRole?: string | null;
};

export function buildSummaryRenderPlan({ raw, companyName }: TBuildPlanInput): TSummaryRenderPlan | null {
  const { tree, prose } = ingestSummaryPayload(raw);
  if (!tree && !prose) return null;

  const nodes = tree ? analyzeTree(tree) : [];
  const consumedKeys = new Set<string>();

  // --- Hero ---------------------------------------------------------
  const heroSummaryNode = nodes.find(isHeroSummaryNode) ?? null;
  if (heroSummaryNode) consumedKeys.add(heroSummaryNode.key);

  const heroTitle = findHeroTitle(nodes);
  const heroSubtitle = findHeroSubtitle(nodes);
  for (const node of nodes) {
    if (node.path.length === 1 && node.concept === "identity" && node.dataType !== "object") {
      const tokens = tokenizeKey(node.key);
      if (tokens.includes("name") || tokens.includes("display") || tokens.includes("type")) {
        consumedKeys.add(node.key);
      }
    }
  }

  // Break wall-of-text summaries into scannable paragraphs (lossless).
  const heroSummary = heroSummaryNode
    ? formatProseParagraphs(sanitizeText(String(heroSummaryNode.value)))
    : prose
      ? formatProseParagraphs(sanitizeText(prose))
      : null;

  // --- Recommendations ----------------------------------------------
  const recommendations = collectRecommendations(nodes);
  recommendations.keys.forEach((key) => consumedKeys.add(key));

  // --- Metrics & insights -------------------------------------------
  const metricSourceProse = [heroSummary ?? "", prose ?? ""].join("\n");
  const metrics = extractMetrics(nodes, metricSourceProse || null);
  const insights = deriveInsights(nodes, metrics);

  // --- Sections ------------------------------------------------------
  const metricIds = new Set(metrics.map((m) => m.id));
  const { sections, hiddenCount } = buildLayout(nodes, consumedKeys, metricIds);

  return {
    hero: {
      title: heroTitle ?? companyName ?? null,
      subtitle: heroSubtitle,
      summary: heroSummary,
    },
    metrics,
    insights,
    sections,
    recommendations: recommendations.texts,
    hiddenFieldCount: hiddenCount,
  };
}
